import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import allImages from "../../../assets/images-import";
import ImageSkeleton from "../Loaders/ImageSkeleton";
import "./image-upload.css";

const DEFAULT_ACCEPTED_TYPES = [".jpg", ".jpeg", ".png", ".webp"];

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getFileNameFromUrl = (url) => {
  if (!url) return "";
  try {
    const clean = url.split("?")[0].split("#")[0];
    const name = clean.substring(clean.lastIndexOf("/") + 1);
    return decodeURIComponent(name) || "Current image";
  } catch {
    return "Current image";
  }
};

const buildFormatLabel = (acceptedTypes) =>
  acceptedTypes
    .map((t) => t.replace(/^\./, "").toUpperCase())
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(" • ");

/**
 * Reusable image upload field — consistent dropzone + preview across the admin.
 * Purely presentational: it surfaces the selected File (or null on remove) via
 * onChange. Validation, FormData building and API flows stay in the parent.
 */
const ImageUpload = ({
  label,
  required = false,
  value = null,
  onChange,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  recommendedSize = "Max 2MB",
  previewUrl = "",
  previewFileName = "",
  sourceBadge = null,
  onEmptyClick,
  onReplaceClick,
  onFileDrop,
  onRemoveClick,
  uploading = false,
  mediaLibraryMode = false,
  error = "",
  id,
  className = "",
}) => {
  const reactId = useId();
  const inputId = id || `image-upload-${reactId}`;
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const [imgReady, setImgReady] = useState(false);

  const hasImage = Boolean(previewUrl);
  const acceptAttr = (acceptedTypes || DEFAULT_ACCEPTED_TYPES).join(",");
  const formatLabel = buildFormatLabel(acceptedTypes || DEFAULT_ACCEPTED_TYPES);

  useEffect(() => {
    if (!previewUrl) {
      setDimensions(null);
      setImgReady(false);
      return undefined;
    }
    let cancelled = false;
    setImgReady(false);
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setImgReady(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setDimensions(null);
        setImgReady(true);
      }
    };
    img.src = previewUrl;
    return () => {
      cancelled = true;
    };
  }, [previewUrl]);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleEmptyActivate = useCallback(() => {
    if (typeof onEmptyClick === "function") {
      onEmptyClick();
      return;
    }
    openPicker();
  }, [onEmptyClick, openPicker]);

  const handleReplace = useCallback(() => {
    if (typeof onReplaceClick === "function") {
      onReplaceClick();
      return;
    }
    handleEmptyActivate();
  }, [onReplaceClick, handleEmptyActivate]);

  const emitFile = useCallback(
    (file) => {
      if (typeof onChange === "function") onChange(file || null);
    },
    [onChange]
  );

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) emitFile(file);
    e.target.value = "";
  };

  const handleRemove = () => {
    if (typeof onRemoveClick === "function") {
      onRemoveClick();
      return;
    }
    if (inputRef.current) inputRef.current.value = "";
    emitFile(null);
  };

  const handlePreview = () => {
    if (previewUrl) window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (typeof onFileDrop === "function") {
      await onFileDrop(file);
      return;
    }
    emitFile(file);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleEmptyActivate();
    }
  };

  const fileName =
    previewFileName || value?.name || getFileNameFromUrl(previewUrl);
  const fileSize = value?.size ? formatBytes(value.size) : "";

  return (
    <div className={`xc-image-upload ${className}`.trim()}>
      {label ? (
        <label htmlFor={inputId} className="form-label xc-image-upload__label">
          {label}
          {required ? <span className="required-field"> *</span> : null}
        </label>
      ) : null}

      {!mediaLibraryMode ? (
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={acceptAttr}
          className="xc-image-upload__input"
          onChange={handleInputChange}
        />
      ) : null}

      {hasImage ? (
        <div
          className={`xc-image-upload__card xc-image-upload__preview-card ${
            error ? "is-error" : ""
          }`}
        >
          <div className="xc-image-upload__preview">
            {sourceBadge ? (
              <span
                className={`xc-image-upload__badge xc-image-upload__badge--${sourceBadge}`}
              >
                <>
                  <i className="ri-folder-image-line" aria-hidden="true" />
                  Media Library
                </>
              </span>
            ) : null}
            {!imgReady ? (
              <ImageSkeleton width="100%" height="100%" />
            ) : null}
            <img
              src={previewUrl}
              alt={label || "Selected"}
              style={{ display: imgReady ? "block" : "none" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = allImages.DefultImage;
              }}
            />
          </div>

          <div className="xc-image-upload__meta">
            <p className="xc-image-upload__filename" title={fileName}>
              {fileName}
            </p>
            <div className="xc-image-upload__filemeta">
              {fileSize ? <span>{fileSize}</span> : null}
              {dimensions ? (
                <span>
                  {dimensions.width} × {dimensions.height}px
                </span>
              ) : null}
            </div>
          </div>

          <div className="xc-image-upload__actions">
            <button
              type="button"
              className="xc-iu-btn xc-iu-btn--primary"
              onClick={handleReplace}
            >
              <i className="ri-image-edit-line" aria-hidden="true" />
              Replace
            </button>
            <button
              type="button"
              className="xc-iu-btn xc-iu-btn--ghost"
              onClick={handlePreview}
            >
              <i className="ri-eye-line" aria-hidden="true" />
              Preview
            </button>
            <button
              type="button"
              className="xc-iu-btn xc-iu-btn--danger"
              onClick={handleRemove}
            >
              <i className="ri-delete-bin-6-line" aria-hidden="true" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={`xc-image-upload__card xc-image-upload__dropzone ${
            isDragging ? "is-dragging" : ""
          } ${uploading ? "is-uploading" : ""} ${error ? "is-error" : ""}`}
          onClick={uploading ? undefined : handleEmptyActivate}
          onKeyDown={handleKeyDown}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploading ? (
            <>
              <span className="xc-image-upload__icon">
                <span className="xc-image-upload__spinner" aria-hidden="true" />
              </span>
              <span className="xc-image-upload__dz-title">Uploading image...</span>
              <span className="xc-image-upload__dz-subtitle">
                Saving to Media Library
              </span>
            </>
          ) : (
            <>
              <span className="xc-image-upload__icon">
                <i className="ri-image-add-line" aria-hidden="true" />
              </span>
              <span className="xc-image-upload__dz-title">
                Drag &amp; Drop Image Here
              </span>
              <span className="xc-image-upload__dz-or">or</span>
              <span className="xc-image-upload__dz-browse">Browse Files</span>
              <span className="xc-image-upload__dz-formats">{formatLabel}</span>
              <span className="xc-image-upload__dz-rec">
                Recommended: {recommendedSize}
              </span>
            </>
          )}
        </div>
      )}

      <p className="xc-image-upload__hint">
        {formatLabel} • Recommended: {recommendedSize}
      </p>
      {error ? <p className="xc-image-upload__error">{error}</p> : null}
    </div>
  );
};

export default ImageUpload;
